import PageLoader from "components/PageLoader"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate } from "react-router-dom"
import {
  Code,
  Settings,
  Paintbrush,
  Pencil,
  Plus,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useMerchantsQuery } from "hooks/query/useMerchantQuery.ts"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

const PAGE_SIZE_OPTIONS = [30, 50, 100] as const

export const MerchantsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: merchants = [], isLoading } = useMerchantsQuery()

  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<number>(30)

  const filtered = useMemo(() => {
    if (!search.trim()) return merchants
    const q = search.toLowerCase()
    return merchants.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) ||
        m.email?.toLowerCase().includes(q) ||
        m.domain?.toLowerCase().includes(q)
    )
  }, [merchants, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePageIndex = Math.min(page, totalPages - 1)
  const paginated = filtered.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const actions = [
    {
      icon: Pencil,
      label: t("merchantsPage.edit"),
      path: (id: string) => `/merchants/edit?id=${id}`,
      testId: "merchant-action-edit",
    },
    {
      icon: Paintbrush,
      label: t("merchantsPage.uiCustomization"),
      path: (id: string) => `/ui-customization/edit?id=${id}`,
      testId: "merchant-action-ui-customization",
    },
    {
      icon: Settings,
      label: t("merchantsPage.configuration"),
      path: (id: string) => `/configuration?id=${id}`,
      testId: "merchant-action-configuration",
    },
    {
      icon: Code,
      label: t("merchantsPage.integration"),
      path: (id: string) => `/integration-info?id=${id}`,
      testId: "merchant-action-integration",
    },
    {
      icon: Eye,
      label: t("merchantsPage.analytics"),
      path: (id: string) => `/analytics?id=${id}`,
      testId: "merchant-action-analytics",
    },
  ]

  return (
    <TooltipProvider delayDuration={300}>
      <div className="p-6" data-testid="merchants-page">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="merchants-title">
              {t("merchantsPage.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("merchantsPage.subtitle", { count: filtered.length })}
            </p>
          </div>
          <Button size="sm" asChild>
            <RouterLink to="/merchants/add" data-testid="merchants-add">
              <Plus className="mr-1.5 size-3.5" />
              {t("merchantsPage.addMerchant")}
            </RouterLink>
          </Button>
        </div>

        {/* Search */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="merchants-search"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by name, email, or domain..."
              className="pl-9"
            />
          </div>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => {
              setPageSize(Number(v))
              setPage(0)
            }}
          >
            <SelectTrigger className="w-[100px]" data-testid="merchants-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} rows
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {isLoading ? (
          <PageLoader />
        ) : paginated.length > 0 ? (
          <Card data-testid="merchants-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-medium">{t("merchantsPage.name")}</TableHead>
                  <TableHead className="font-medium">{t("merchantsPage.email")}</TableHead>
                  <TableHead className="font-medium">{t("merchantsPage.domain")}</TableHead>
                  <TableHead className="w-[220px] text-right font-medium">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginated.map((merchant) => (
                  <TableRow
                    key={merchant.id}
                    data-testid="merchant-row"
                    data-merchant-id={merchant.id}
                  >
                    <TableCell className="font-medium">{merchant.name}</TableCell>
                    <TableCell className="text-muted-foreground">{merchant.email}</TableCell>
                    <TableCell className="text-muted-foreground">{merchant.domain}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {actions.map(({ icon: Icon, label, path, testId }) => (
                          <Tooltip key={label}>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8"
                                onClick={() => navigate(path(merchant.id))}
                                data-testid={testId}
                              >
                                <Icon className="size-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p>{label}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <p className="text-sm text-muted-foreground">
                  {safePageIndex * pageSize + 1}–
                  {Math.min((safePageIndex + 1) * pageSize, filtered.length)} of {filtered.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-8"
                    disabled={safePageIndex === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <span className="min-w-[80px] text-center text-sm text-muted-foreground">
                    {safePageIndex + 1} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-8"
                    disabled={safePageIndex >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ) : (
          <Card className="border-dashed" data-testid="merchants-empty-state">
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 py-12">
              <p className="font-medium">No merchants found</p>
              <p className="text-sm text-muted-foreground">
                {search ? "Try adjusting your search" : "Add your first merchant to get started"}
              </p>
            </div>
          </Card>
        )}
      </div>
    </TooltipProvider>
  )
}
